import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("accessToken"));
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/";

  // Set up axios interceptor for automatic token refresh
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Don't attempt refresh for auth endpoints (login, register, refresh)
        const isAuthEndpoint = originalRequest.url?.includes('/api/auth/login') ||
                              originalRequest.url?.includes('/api/auth/register') ||
                              originalRequest.url?.includes('/api/auth/refresh');

        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
          originalRequest._retry = true;

          // Only attempt refresh if we have a refresh token
          const refreshTokenValue = localStorage.getItem("refreshToken");
          if (!refreshTokenValue) {
            // No refresh token available, just reject the error
            logout();
            return Promise.reject(error);
          }

          try {
            const newToken = await refreshToken();
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return axios(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, logout user
            logout();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_URL}api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (response.data.success) {
            setUser(response.data.data.salesperson);
            setIsAuthenticated(true);
          } else {
            // Token is invalid, clear it
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
            setToken(null);
            setIsAuthenticated(false);
          }
        } catch (error) {
          console.error("Auth check failed:", error);
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          setToken(null);
          setIsAuthenticated(false);
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, [token, API_URL]);

  // Background token refresh - refresh token before expiration
  useEffect(() => {
    if (!token || !isAuthenticated) return;

    const refreshInterval = setInterval(async () => {
      try {
        const newToken = await refreshToken();
        if (newToken) {
          console.log("Token refreshed automatically");
        }
      } catch (error) {
        console.error("Background token refresh failed:", error);
        // Don't logout on background refresh failure, let the interceptor handle it
      }
    }, 90 * 60 * 1000); // Refresh every 90 minutes (tokens expire in 2 hours)

    return () => clearInterval(refreshInterval);
  }, [token, isAuthenticated]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}api/auth/login`, {
        email,
        password,
      });

      if (response.data.success) {
        const {
          salesperson: userData,
          accessToken,
          refreshToken,
        } = response.data.data;

        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", refreshToken);

        setToken(accessToken);
        setUser(userData);
        setIsAuthenticated(true);

        return { success: true, user: userData };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error("Login error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Login failed",
      };
    }
  };

  const register = async (email, password, name, role = "salesperson") => {
    try {
      const response = await axios.post(`${API_URL}api/auth/register`, {
        email,
        password,
        name,
        role,
      });

      if (response.data.success) {
        const {
          salesperson: userData,
          accessToken,
          refreshToken,
        } = response.data.data;

        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", refreshToken);

        setToken(accessToken);
        setUser(userData);
        setIsAuthenticated(true);

        return { success: true, user: userData };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error("Registration error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Registration failed",
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  const refreshToken = async () => {
    const refreshTokenValue = localStorage.getItem("refreshToken");
    if (!refreshTokenValue) {
      // Don't throw error here, just return null
      // The caller should handle the case where there's no refresh token
      return null;
    }

    try {
      const response = await axios.post(`${API_URL}api/auth/refresh`, {
        refreshToken: refreshTokenValue,
      });

      if (response.data.success) {
        const { accessToken } = response.data.data;
        localStorage.setItem("accessToken", accessToken);
        setToken(accessToken);
        return accessToken;
      } else {
        throw new Error("Token refresh failed");
      }
    } catch (error) {
      console.error("Token refresh error:", error);
      logout();
      throw error;
    }
  };

  const getAccessTokenSilently = async () => {
    // Return current token if available
    if (token) {
      return token;
    }

    // Try to refresh token if no current token
    try {
      const newToken = await refreshToken();
      if (newToken) {
        return newToken;
      }
      throw new Error("Unable to get access token");
    } catch (error) {
      throw new Error("Unable to get access token");
    }
  };

  // Role checking utility functions
  const hasRole = (role) => {
    return user?.role === role;
  };

  const isSuperAdmin = () => {
    return hasRole("super_admin");
  };

  const isAdmin = () => {
    return hasRole("admin");
  };

  const isSalesperson = () => {
    return hasRole("salesperson");
  };

  const hasMinimumRole = (requiredRole) => {
    const roleHierarchy = {
      salesperson: 1,
      admin: 2,
      super_admin: 3,
    };

    const userRoleLevel = roleHierarchy[user?.role] || 0;
    const requiredRoleLevel = roleHierarchy[requiredRole] || 0;

    return userRoleLevel >= requiredRoleLevel;
  };

  const value = {
    user,
    token,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    getAccessTokenSilently,
    hasRole,
    isSuperAdmin,
    isAdmin,
    isSalesperson,
    hasMinimumRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
